import { useCallback, useRef } from 'react'

export type WalkDownInstance = {
  runWalk?: (type: string, args?: unknown) => Promise<unknown> | unknown
  walkDown?: (type: string, args?: unknown) => Promise<unknown> | unknown
}

export type WalkDownAggregator = {
  collectRef: (key: unknown) => (instance: WalkDownInstance | null) => void
  walkDown: (type: string, args?: unknown) => Promise<unknown[]>
}

/**
 * Function-component analog of `WalkDownMixin`. A list-style parent uses
 * `collectRef(key)` as the ref callback for each child it renders; calling
 * `walkDown(type, args)` then recurses into each collected child's
 * `runWalk`/`walkDown` and resolves with the aggregated results.
 *
 * Children that don't expose either method (e.g. function components from
 * browse-entry that aren't on the walkDown chain) are skipped silently.
 */
export function useWalkDownAggregator(): WalkDownAggregator {
  const refsRef = useRef<Map<unknown, WalkDownInstance>>(new Map())

  const collectRef = useCallback(
    (key: unknown) => (instance: WalkDownInstance | null) => {
      if (instance) refsRef.current.set(key, instance)
      else refsRef.current.delete(key)
    },
    [],
  )

  const walkDown = useCallback(
    (type: string, args?: unknown): Promise<unknown[]> => {
      return Promise.all(
        Array.from(refsRef.current.values()).map((r) => {
          if (typeof r.runWalk === 'function') return r.runWalk(type, args)
          if (typeof r.walkDown === 'function') return r.walkDown(type, args)
          return undefined
        }),
      )
    },
    [],
  )

  return { collectRef, walkDown }
}
